import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Save, ArrowLeft, Navigation, Ruler } from 'lucide-react';
import { useObras } from '../../context/ObrasContextSupabase';
import DesenharTrajeto from '../../../shared/components/mapa/DesenharTrajeto';

const CadastroObra = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { adicionarObra, atualizarObra, obterObraPorId } = useObras();
    const [mostrarMapa, setMostrarMapa] = useState(false);
    const [trechos, setTrechos] = useState([]);

    const { register, handleSubmit, setValue, formState: { errors } } = useForm({
        defaultValues: {
            nome: '',
            empreiteiro: '',
            valor: '',
            status: 'Planejada',
            dataInicio: '',
            dataPrevisaoTermino: '',
            observacoes: '',
            contrato: '',
            licitacao: '',
            processo: '',
            fiscaisObras: '',
            gestor: '',
            totalMedicoes: 0,
            trechos: []
        }
    });

    useEffect(() => {
        if (id) {
            const obra = obterObraPorId(id);
            if (obra) {
                Object.keys(obra).forEach(key => {
                    if (key === 'trechos') {
                        setTrechos(obra[key] || []);
                    } else {
                        setValue(key, obra[key]);
                    }
                });
            }
        }
    }, [id, obterObraPorId, setValue]);

    const onSubmit = async (data) => {
        try {
            const dadosCompletos = {
                ...data,
                trechos: trechos
            };

            if (id) {
                await atualizarObra(id, dadosCompletos);
            } else {
                await adicionarObra(dadosCompletos);
            }
            navigate('/obras');
        } catch (error) {
            console.error('Erro ao salvar obra:', error);
        }
    };

    const abrirGerenciadorTrechos = () => {
        setMostrarMapa(true);
    };

    const handleTrechosAtualizados = (novosTrechos) => {
        setTrechos(novosTrechos);
        setMostrarMapa(false);
    };

    const contarPontosTotais = (trechos) => {
        return trechos.reduce((total, trecho) => {
            let pontos = trecho.pontos.length;
            if (trecho.derivacoes) {
                pontos += trecho.derivacoes.reduce((subTotal, derivacao) => subTotal + derivacao.pontos.length, 0);
            }
            return total + pontos;
        }, 0);
    };

    // Função para calcular a distância total em metros de todos os trechos
    const calcularDistanciaTotal = (trechos) => {
        return trechos.reduce((total, trecho) => {
            let distancia = trecho.distanciaMetros || 0;
            if (trecho.derivacoes) {
                distancia += trecho.derivacoes.reduce((subTotal, derivacao) =>
                    subTotal + (derivacao.distanciaMetros || 0), 0);
            }
            return total + distancia;
        }, 0);
    };

    // Formatar distância para exibição (km com 2 casas decimais)
    const formatarDistancia = (metros) => {
        if (!metros) return '0 km';
        return `${(metros / 1000).toFixed(2)} km`;
    };
    
    // Formatar valor monetário
    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    return (
        <div>
            <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                    <button
                        type="button"
                        className="button secondary"
                        onClick={() => navigate('/obras')}
                    >
                        <ArrowLeft size={18} />
                        Voltar
                    </button>
                    <h2>{id ? 'Editar Obra' : 'Nova Obra'}</h2>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="form-group">
                        <label>Nome da Obra *</label>
                        <input
                            type="text"
                            {...register('nome', { required: 'Nome da obra é obrigatório' })}
                        />
                        {errors.nome && <span style={{ color: 'red', fontSize: '14px' }}>{errors.nome.message}</span>}
                    </div>

                    <div className="form-row-4">
                        <div className="form-group">
                            <label>Empreiteiro Responsável *</label>
                            <input
                                type="text"
                                {...register('empreiteiro', { required: 'Empreiteiro é obrigatório' })}
                            />
                            {errors.empreiteiro && <span style={{ color: 'red', fontSize: '14px' }}>{errors.empreiteiro.message}</span>}
                        </div>

                        <div className="form-group">
                            <label>Valor da Obra (R$) *</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                {...register('valor', {
                                    required: 'Valor é obrigatório',
                                    min: { value: 0, message: 'Valor deve ser positivo' }
                                })}
                            />
                            {errors.valor && <span style={{ color: 'red', fontSize: '14px' }}>{errors.valor.message}</span>}
                        </div>

                        <div className="form-group">
                            <label>Status *</label>
                            <select {...register('status', { required: 'Status é obrigatório' })}>
                                <option value="Planejada">Planejada</option>
                                <option value="Em execução">Em execução</option>
                                <option value="Finalizada">Finalizada</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Data de Início *</label>
                            <input
                                type="date"
                                {...register('dataInicio', { required: 'Data de início é obrigatória' })}
                            />
                            {errors.dataInicio && <span style={{ color: 'red', fontSize: '14px' }}>{errors.dataInicio.message}</span>}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Previsão de Término *</label>
                            <input
                                type="date"
                                {...register('dataPrevisaoTermino', { required: 'Data de previsão é obrigatória' })}
                            />
                            {errors.dataPrevisaoTermino && <span style={{ color: 'red', fontSize: '14px' }}>{errors.dataPrevisaoTermino.message}</span>}
                        </div>
                        
                        <div className="form-group">
                            <label>Observações</label>
                            <textarea
                                {...register('observacoes')}
                                placeholder="Informações adicionais sobre a obra..."
                            />
                        </div>
                    </div>

                    {/* Novos campos adicionados */}
                    <div className="form-row-3">
                        <div className="form-group">
                            <label>Número do Contrato</label>
                            <input
                                type="text"
                                {...register('contrato')}
                                placeholder="Ex: CT-2024-001"
                            />
                        </div>

                        <div className="form-group">
                            <label>Número da Licitação</label>
                            <input
                                type="text"
                                {...register('licitacao')}
                                placeholder="Ex: PP-2024-001"
                            />
                        </div>

                        <div className="form-group">
                            <label>Número do Processo</label>
                            <input
                                type="text"
                                {...register('processo')}
                                placeholder="Ex: PROC-2024-001"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Gestor da Obra</label>
                            <input
                                type="text"
                                {...register('gestor')}
                                placeholder="Nome do gestor responsável"
                            />
                        </div>

                        <div className="form-group">
                            <label>Fiscais de Obras</label>
                            <textarea
                                {...register('fiscaisObras')}
                                placeholder="Lista dos fiscais responsáveis (separados por vírgula)"
                                rows="3"
                            />
                        </div>
                    </div>

                    {/* Campo calculado - Total de Medições */}
                    <div className="form-group">
                        <label>Total de Medições</label>
                        <div className="medida-display" style={{ 
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            color: '#495057'
                        }}>
                            {id && obterObraPorId(id) ? (
                                <>
                                    {parseFloat(obterObraPorId(id).totalMedicoes || 0).toFixed(2)}
                                    <span style={{ marginLeft: '5px', color: '#666' }}>
                                        {obterObraPorId(id)?.itens && obterObraPorId(id).itens.length > 0 && obterObraPorId(id).itens[0].unidade ? 
                                            obterObraPorId(id).itens[0].unidade : 
                                            'un'
                                        }
                                    </span>
                                </>
                            ) : (
                                <>
                                    0.00
                                    <span style={{ marginLeft: '5px', color: '#666' }}>un</span>
                                </>
                            )}
                        </div>
                        <input
                            type="hidden"
                            {...register('totalMedicoes')}
                        />
                        <small style={{ color: '#666', fontSize: '12px' }}>
                            Este valor é calculado automaticamente com base na soma de todas as quantidades medidas nos itens da obra.
                        </small>
                    </div>

                    <div className="form-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <label>Trechos da Obra *</label>
                            <button
                                type="button"
                                className="button"
                                onClick={abrirGerenciadorTrechos}
                            >
                                <Navigation size={16} />
                                Roteamento Inteligente
                            </button>
                        </div>

                        {trechos.length === 0 ? (
                            <div style={{
                                padding: '20px',
                                textAlign: 'center',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                border: '2px dashed #dee2e6'
                            }}>
                                <p style={{ margin: 0, color: '#666' }}>
                                    Nenhum trecho adicionado. Clique em "Roteamento Inteligente" para começar.
                                </p>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '15px' }}>
                                {trechos.map((trecho) => (
                                    <div key={trecho.id} style={{
                                        padding: '15px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        border: '1px solid #dee2e6'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <div style={{ flex: 1 }}>
                                                <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>
                                                    {trecho.nome}
                                                </h4>
                                                <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>
                                                    {trecho.pontos.length} pontos no trecho principal
                                                    {trecho.distanciaMetros && (
                                                        <span style={{ marginLeft: '8px' }}>
                                                            • <Ruler size={14} style={{ verticalAlign: 'middle', marginRight: '2px' }} />
                                                            {formatarDistancia(trecho.distanciaMetros)}
                                                        </span>
                                                    )}
                                                </p>
                                                {trecho.derivacoes && trecho.derivacoes.length > 0 && (
                                                    <div style={{ fontSize: '12px', color: '#28a745' }}>
                                                        <strong>{trecho.derivacoes.length} derivação(ões):</strong>
                                                        <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                                                            {trecho.derivacoes.map(derivacao => (
                                                                <li key={derivacao.id}>
                                                                    {derivacao.nome} ({derivacao.pontos.length} pontos)
                                                                    {derivacao.distanciaMetros && (
                                                                        <span style={{ marginLeft: '5px' }}>
                                                                            • <Ruler size={10} style={{ verticalAlign: 'middle', marginRight: '2px' }} />
                                                                            {formatarDistancia(derivacao.distanciaMetros)}
                                                                        </span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#666', textAlign: 'right' }}>
                                                <strong>Total: {contarPontosTotais([trecho])} pontos</strong>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                            <strong>Resumo:</strong> {trechos.length} trecho(s) • {contarPontosTotais(trechos)} pontos totais
                            {trechos.length > 0 && (
                                <span style={{ marginLeft: '10px', fontWeight: 'bold', color: '#28a745' }}>
                                    • <Ruler size={14} style={{ verticalAlign: 'middle', marginRight: '2px' }} />
                                    Extensão total: {formatarDistancia(calcularDistanciaTotal(trechos))}
                                </span>
                            )}
                        </div>

                        {trechos.length === 0 && (
                            <span style={{ color: 'red', fontSize: '14px', marginTop: '8px', display: 'block' }}>
                                É necessário adicionar pelo menos um trecho para a obra
                            </span>
                        )}
                    </div>

                    {/* Nota sobre medições */}
                    <div className="form-group" style={{ marginTop: '30px' }}>
                        <div style={{ 
                            padding: '15px', 
                            backgroundColor: '#f0f8ff', 
                            borderRadius: '8px',
                            border: '1px solid #b8daff',
                            marginBottom: '20px'
                        }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#004085', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>Medições e Itens</span>
                            </h4>
                            <p style={{ margin: '0', color: '#004085' }}>
                                Após cadastrar a obra, você poderá gerenciar os itens e medições através do botão "Medições" na lista de obras.
                            </p>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '30px' }}>
                        <button
                            type="button"
                            className="button secondary"
                            onClick={() => navigate('/obras')}
                        >
                            Cancelar
                        </button>
                        <button type="submit" className="button">
                            <Save size={18} />
                            {id ? 'Atualizar Obra' : 'Cadastrar Obra'}
                        </button>
                    </div>
                </form>
            </div>

            {mostrarMapa && (
                <DesenharTrajeto
                    onTrechosAtualizados={handleTrechosAtualizados}
                    onFechar={() => setMostrarMapa(false)}
                    titulo="Roteamento Incremental - Trechos da Obra"
                    trechosIniciais={trechos}
                />
            )}
        </div>
    );
};

export default CadastroObra;